export interface ParsedCitation {
  number: number;
  filename: string;
  page: string;
  sourceText?: string;
}

export interface ParsedCellText {
  text: string;
  citations: ParsedCitation[];
}

export interface CitationMatch {
  match: string;
  content: string;
  start: number;
  end: number;
}

// A cited source with one of these extensions is a document.
const DOCUMENT_EXTENSION_PATTERN = /\.(?:pdf|docx|pptx|xlsx)\b/;

export function findCitationsWithNestedBrackets(text: string): CitationMatch[] {
  const results: CitationMatch[] = [];
  let i = 0;

  while (i < text.length) {
    if (text[i] === "[") {
      let bracketCount = 1;
      let j = i + 1;
      let content = "";

      while (j < text.length && bracketCount > 0) {
        if (text[j] === "[") bracketCount++;
        else if (text[j] === "]") bracketCount--;
        if (bracketCount > 0) content += text[j];
        j++;
      }

      const hasFileExtension = DOCUMENT_EXTENSION_PATTERN.test(content);
      const isNotMarkdownLink = j >= text.length || text[j] !== "(";

      if (bracketCount === 0 && hasFileExtension && isNotMarkdownLink) {
        results.push({ match: text.substring(i, j), content, start: i, end: j });
        i = j;
      } else {
        i++;
      }
    } else {
      i++;
    }
  }

  return results;
}

// A grounded citation is [<source>#page=N||<quote>]; an artefact has no page, so either
// part can be missing. Plain brackets have neither and are left alone, as is a markdown
// link's text. Either bracket may be markdown-escaped.
const GROUNDED_CITATION_PATTERN = /[ \t]*\\?\[([^[\]#|]+?)(#page=\d+(?:-\d+)?)?(\|\|[^\]]*)?\\?\](?!\()/g;

// Temporary: only a document can be referenced and opened, so a grounded citation to
// anything else (an artefact, cited by label, uid or row text) is removed, with the
// space before it so a sentence doesn't end " .".
export function dropUnsupportedCitations(text: string): string {
  return text.replace(GROUNDED_CITATION_PATTERN, (citation, source: string, page?: string, quote?: string) => {
    if (!page && !quote) return citation;
    return DOCUMENT_EXTENSION_PATTERN.test(source.replace(/\\(.)/g, "$1")) ? citation : "";
  });
}

/** Generated report text: citations that can't be referenced are dropped before parsing. */
export function parseReportCitations(raw: string): ParsedCellText {
  return parseCellCitations(dropUnsupportedCitations(raw));
}

export function parseCellCitations(raw: string): ParsedCellText {
  if (!raw) return { text: "", citations: [] };

  const citations: ParsedCitation[] = [];
  const matches = findCitationsWithNestedBrackets(raw);

  let parsedText = raw;
  let offset = 0;
  let currentIndex = 1;

  matches.forEach(({ match, content, start, end }) => {
    const adjustedStart = start - offset;
    const adjustedEnd = end - offset;

    const parts = content.split("||");
    const citationText = parts[0] ?? "";
    const sourceText = parts[1];
    const filename = (citationText.split("#")[0] ?? "").replace(/\\(.)/g, '$1');

    let page = "";
    if (citationText.includes("#page=")) {
      const pageMatch = /#page=(\d+)/.exec(citationText);
      if (pageMatch) page = `#page=${pageMatch[1]}`;
    } else if (citationText.includes("#")) {
      page = citationText.substring(citationText.indexOf("#"));
    }

    const number = currentIndex++;
    citations.push({ number, filename, page, sourceText });

    // If the citation bracket is preceded by a backslash (markdown escape like \[file.pdf\]),
    // consume the backslash too so it doesn't appear before the superscript placeholder.
    const hasPrecedingBackslash = adjustedStart > 0 && parsedText[adjustedStart - 1] === "\\";
    const effectiveStart = hasPrecedingBackslash ? adjustedStart - 1 : adjustedStart;

    const placeholder = `^${number}^`;
    parsedText =
      parsedText.substring(0, effectiveStart) +
      placeholder +
      parsedText.substring(adjustedEnd);
    offset += (adjustedEnd - effectiveStart) - placeholder.length;
  });

  return { text: parsedText, citations };
}

export function extractCitationBrackets(text: string): string[] {
  if (!text) return [];
  const matches = findCitationsWithNestedBrackets(text);
  return matches.map(m => m.match);
}

export function removeCitationBrackets(text: string): string {
  if (!text) return "";

  const matches = findCitationsWithNestedBrackets(text);
  let result = text;
  let offset = 0;

  matches.forEach(({ start, end }) => {
    const adjustedStart = start - offset;
    const adjustedEnd = end - offset;
    result = result.substring(0, adjustedStart) + result.substring(adjustedEnd);
    offset += (end - start);
  });

  return result;
}
